import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';

const Standings = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tournament, setTournament] = useState(null);
    const [toast, setToast] = useState(null); 
    const [isGenerating, setIsGenerating] = useState(false);

    // --- PHASE 5: STRICT ROLE CHECKS ---
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = (storedUser.id || storedUser._id)?.toString();
    const userRole = storedUser.role || 'player';

    useEffect(() => {
        fetchTournament();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchTournament = async () => {
        try {
            const { data } = await api.get(`/tournaments/${id}`);
            setTournament(data);
        } catch (err) {
            console.error("Failed to load standings", err);
        }
    };

    // --- PHASE 4: UNIVERSAL ENGINE SETUP ---
    const gameType = tournament?.settings?.gameType || 'Catan';
    const isCatan = gameType === 'Catan' || gameType === 'Catan Seafarers';
    const format = tournament?.settings?.format || 'Swiss';
    
    // STRICT CHECK: Admin OR (Acting as Master AND owns this specific tournament)
    const masterIdStr = tournament?.masterId ? (tournament.masterId._id || tournament.masterId).toString() : '';
    const isMaster = userRole === 'admin' || (userRole === 'master' && masterIdStr && userId && masterIdStr === userId);
    // ---------------------------------------

    const currentRoundData = tournament?.rounds?.find(r => r.roundNumber === tournament.currentRound);
    const isCurrentRoundComplete = currentRoundData 
        ? currentRoundData.groups.every(group => group.isSubmitted === true) 
        : true;

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500); 
    };

    const handleCopyPublicLink = () => {
        const publicUrl = `${window.location.origin}/public/tournament/${id}`;
        navigator.clipboard.writeText(publicUrl);
        showToast("🔗 Public link copied to clipboard!", "success");
    };

    const exportToCSV = (sortedPlayers) => {
        // Dynamic CSV Headers based on Game Type
        const headers = ["Rank", "Name", isCatan ? "Total Win Pts" : "Total Wins"];
        if (isCatan) {
            headers.push("Win %", "Total Game Pts");
        }

        const rows = sortedPlayers.map((p, index) => {
            const row = [index + 1, p.name, p.totalWinPts || 0];
            if (isCatan) {
                row.push((p.totalPercentage || p.percentage || 0).toFixed(2), p.totalGamePts || 0);
            }
            return row.join(",");
        });
        
        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        
        const a = document.createElement("a");
        a.setAttribute("hidden", "");
        a.setAttribute("href", url);
        a.setAttribute("download", `${tournament.name}_Final_Standings.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    if (!tournament) return (
        <div className="flex justify-center items-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );

    const currentRound = tournament.currentRound || 1;
    const maxRounds = tournament.settings?.maxRounds || 3;
    const tieOrder = tournament.settings?.tieBreakerOrder || ['totalWinPts', 'totalPercentage', 'totalGamePts'];

    const sortedPlayers = [...tournament.players].sort((a, b) => {
        for (const criterion of tieOrder) {
            if (b[criterion] !== a[criterion]) {
                return (b[criterion] || 0) - (a[criterion] || 0);
            }
        }
        return 0;
    });
    // --- SMART ROUND CALCULATION ---
    let isLastRound = false;
    if (tournament) {
        const stage = tournament.stage || tournament.settings?.stage;
        if (tournament.settings?.format === 'Official Catan') {
            isLastRound = stage === 'final';
        } else {
            const maxRounds = tournament.settings?.maxRounds || 3;
            const cutToTop = Number(tournament.settings?.cutToTop) || 0;
            
            // If they cut to top 4, Math.log2(4) adds 2 extra rounds!
            const totalRounds = cutToTop > 0 ? maxRounds + Math.log2(cutToTop) : maxRounds;
            isLastRound = tournament.currentRound >= totalRounds;
        }
    }
const handleAdvance = async () => {
        setIsGenerating(true); 
        
        try {
            const response = await api.post(`/tournaments/${id}/advance`);
            
            // We check the backend's explicit message OR our frontend math OR tournament status
            if (response.data.message === 'Tournament completed' || response.data.tournament?.status === 'completed' || response.data.status === 'completed' || isLastRound) {
                showToast("🏆 Tournament Complete!", "success");
                setTimeout(() => navigate('/dashboard'), 2000);
            } else {
                showToast("⚡ Advanced to next round!", "success");
                setTournament(response.data); 
                navigate(`/tournament/${id}/active`);
            }
        } catch (err) {
            showToast(err.response?.data?.message || err.response?.data?.error || "Failed to advance", "error");
        } finally {
            setIsGenerating(false);
        }
    };

    const getPreviousRanks = () => {
        if (currentRound === 1 && tournament.status !== 'completed') return {};

        const lastPlayedRoundNum = tournament.status === 'completed' ? currentRound : currentRound - 1;
        const lastPlayedRound = tournament.rounds.find(r => r.roundNumber === lastPlayedRoundNum);
        
        if (!lastPlayedRound) return {};

        let previousTotals = tournament.players.map(p => {
            let recentWinPts = 0, recentGamePts = 0, recentPct = 0;
            
            lastPlayedRound.groups.forEach(g => {
                const playerInGroup = g.players.find(gp => gp.playerId === p.id);
                if (playerInGroup && playerInGroup.gamePts !== null) {
                    recentWinPts = playerInGroup.winPts || 0;
                    recentGamePts = playerInGroup.gamePts || 0;
                    recentPct = playerInGroup.percentage || 0;
                }
            });

            return {
                id: p.id,
                oldWinPts: (p.totalWinPts || 0) - recentWinPts,
                oldPct: (p.totalPercentage || 0) - recentPct,
                oldGamePts: (p.totalGamePts || 0) - recentGamePts
            };
        });

        previousTotals.sort((a, b) => {
            for (const criterion of tieOrder) {
                const oldCrit = criterion.replace('total', 'old'); 
                if (b[oldCrit] !== a[oldCrit]) return b[oldCrit] - a[oldCrit];
            }
            return 0;
        });

        const oldRankMap = {};
        previousTotals.forEach((p, index) => { oldRankMap[p.id] = index; });
        return oldRankMap;
    };

    const previousRanks = getPreviousRanks();

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {toast && (
                <div className={`fixed top-5 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-2xl font-bold text-white transition-all animate-in fade-in slide-in-from-top-5 ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
                    {toast.message}
                </div>
            )}

            {/* HEADER SECTION */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-black text-gray-900">{tournament.name}</h1>
                        <p className="text-sm font-bold text-blue-600 uppercase tracking-widest flex items-center gap-2">
                            <span>
                                {tournament.settings?.format === 'Official Catan' 
                                    ? `Round ${currentRound} (${tournament.stage || tournament.settings?.stage || 'preliminary'})`
                                    : `Round ${currentRound} / ${maxRounds}`
                                } • {tournament.status}
                            </span>
                        </p>
                        <button onClick={handleCopyPublicLink} className="mt-2 flex items-center gap-2 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700 hover:text-blue-700 px-4 py-2 rounded-xl font-bold text-sm shadow-sm transition-all active:scale-95">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                            Share Live Standings
                        </button>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <button onClick={() => exportToCSV(sortedPlayers)} className="bg-white border-2 border-gray-200 hover:border-blue-500 text-gray-600 hover:text-blue-600 px-4 py-2 rounded-xl text-sm font-bold transition-all">
                            📥 Export CSV
                        </button>
                        
                        {/* THIS BUTTON IS NOW PROTECTED! ONLY MASTERS CAN SEE IT */}
                        {isMaster && (
                            tournament.status === 'completed' ? (
                                <button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-blue-100 transition-all active:scale-95 flex items-center gap-2">
                                    Back to Dashboard
                                </button>
                            ) : (
<button 
    onClick={handleAdvance}
    disabled={isGenerating || !isCurrentRoundComplete}
    className={`px-8 py-3 rounded-xl font-bold transition-all ${
        isGenerating || !isCurrentRoundComplete
            ? 'bg-gray-400 text-gray-100 cursor-not-allowed opacity-80' 
            : !isLastRound ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-100 active:scale-95' : 'bg-gray-900 hover:bg-black text-white shadow-lg active:scale-95'
    }`}

                                >
{isGenerating 
        ? "Processing... ⏳" 
        : !isCurrentRoundComplete 
            ? "Submit scores to advance 🔒" 
            : !isLastRound ? "Generate Next Round ⚡" : "Complete Tournament 🏆"
    }
</button>
                            )
                        )}
                        
                        {/* EVERYONE CAN ALWAYS GO BACK TO MATCHES */}
                        <button onClick={() => navigate(`/tournament/${id}/active`)} className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-4 py-2 rounded-xl text-sm font-bold transition-all">
                            Back to Matches
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 mt-8 overflow-x-auto pb-8">
                {format === 'Knockout' ? (
                    
                    /* --- LIGHT THEME KNOCKOUT BRACKET --- */
                    <div className="flex gap-12 min-w-max items-center justify-start py-10 px-6 bg-gray-50 rounded-3xl border border-gray-200 shadow-inner overflow-x-auto">
                        {tournament.rounds.map((round) => (
                            <div key={round.roundNumber} className="flex flex-col justify-around gap-8 w-64 min-h-[400px]">
                                
                                {/* Round Header */}
                                <div className="text-center bg-white py-3 rounded-xl border border-gray-200 shadow-sm">
                                    <h3 className="text-sm font-black text-blue-600 uppercase tracking-widest">
                                        {round.roundNumber === maxRounds ? '🏆 Finals' : `Round ${round.roundNumber}`}
                                    </h3>
                                </div>
                                
                                {/* Matches */}
                                <div className="flex flex-col justify-around h-full gap-6">
                                    {round.groups.map((match, mIdx) => (
                                        <div key={mIdx} className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-lg border-2 border-transparent hover:border-blue-300">
                                            <div className="bg-gray-50 px-3 py-2 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-100 flex justify-between items-center">
                                                <span>Match {mIdx + 1}</span>
                                                {match.players.length === 1 && <span className="text-blue-600 bg-blue-100 px-2 py-0.5 rounded uppercase">Bye</span>}
                                            </div>
                                            
                                            <div className="divide-y divide-gray-50">
                                                {match.players.map((p, i) => (
                                                    <div key={p.playerId || i} className={`px-4 py-3 flex justify-between items-center ${p.winPts > 0 ? 'bg-blue-50' : ''}`}>
                                                        <span className={`truncate w-36 text-sm ${p.winPts > 0 ? 'font-black text-blue-700' : 'font-bold text-gray-600'}`}>
                                                            {p.name}
                                                        </span>
                                                        <span className={`font-black w-8 h-8 flex items-center justify-center rounded-lg text-sm ${
                                                            p.winPts > 0 
                                                            ? 'bg-blue-600 text-white shadow-sm' 
                                                            : p.gamePts !== null ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-300 border border-gray-100'
                                                        }`}>
                                                            {p.gamePts !== null ? p.gamePts : '-'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                ) : (

                    /* --- ORIGINAL SWISS / ROUND ROBIN TABLE --- */
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                    <th className="px-6 py-4 text-center">Rank</th>
                                    <th className="px-6 py-4">Player Name</th>
                                    <th className="px-6 py-4 text-center">{isCatan ? 'Tournament Pts' : 'Total Points'}</th>
                                    
                                    {isCatan && (
                                        <>
                                            <th className="px-6 py-4 text-center">Win %</th>
                                            <th className="px-6 py-4 text-center">Game Pts</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sortedPlayers.map((player, index) => {
                                    const oldRank = previousRanks[player.id];
                                    let rankChangeUI = null;
                                    
                                    if (oldRank !== undefined) {
                                        const change = oldRank - index; 
                                        if (change > 0) rankChangeUI = <span className="text-green-500 text-xs font-black ml-2 animate-pulse">↑{change}</span>;
                                        else if (change < 0) rankChangeUI = <span className="text-red-500 text-xs font-black ml-2">↓{Math.abs(change)}</span>;
                                        else rankChangeUI = <span className="text-gray-300 text-xs font-black ml-2">-</span>;
                                    }

                                    return (
                                        <tr key={player.id} className="hover:bg-blue-50/30 transition-colors group">
                                            <td className="px-6 py-4 text-center">
                                                {index < 3 ? (
                                                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-black text-sm ${
                                                        index === 0 ? 'bg-yellow-400 text-yellow-900' : 
                                                        index === 1 ? 'bg-gray-300 text-gray-700' : 'bg-orange-400 text-orange-900'
                                                    }`}>
                                                        {index + 1}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 font-bold">{index + 1}</span>
                                                )}
                                            </td>
                                            
                                            <td className="px-6 py-4 font-bold text-gray-800 group-hover:text-blue-600">
                                                <div className="flex items-center">
                                                    {player.name}
                                                    {rankChangeUI}
                                                </div>
                                            </td>
                                            
                                            <td className="px-6 py-4 text-center font-black text-gray-700">{player.totalWinPts || 0}</td>
                                            
                                            {isCatan && (
                                                <>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-black">
                                                            {(player.totalPercentage || player.percentage || 0).toFixed(2)}%
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center font-bold text-gray-400">{player.totalGamePts || 0}</td>
                                                </>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Standings;